import { useDevice } from "@memorang/ui/useDevice";
import { useRouter } from "expo-router";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { DialogContext } from "./DialogContext";
import { sessionConfigDialog } from "./dialogs/SessionConfigDialog";
import { editNameDialog } from "./dialogs/edit-name";
import { editUsernameDialog } from "./dialogs/edit-username";
import { examsFilterDialog } from "./dialogs/exams-filter";
import { purchaseDialog } from "./dialogs/purchase";
import type {
	ActiveDialog,
	DialogName,
	DialogOptionsMap,
	DialogSpec,
	ShowDialogBehavior,
} from "./types";

const dialogRegistry: { [K in DialogName]: DialogSpec<K> } = {
	purchase: purchaseDialog,
	examsFilter: examsFilterDialog,
	editUsername: editUsernameDialog,
	editName: editNameDialog,
	sessionConfig: sessionConfigDialog,
};

type Props = {
	children: ReactNode;
};

export const DialogProvider = ({ children }: Props) => {
	const router = useRouter();
	const { isMobile } = useDevice();
	const [active, setActive] = useState<ActiveDialog>(null);

	const hideDialog = useCallback((name?: DialogName) => {
		setActive((prev) => {
			if (name !== undefined && prev?.name !== name) {
				return prev;
			}
			return null;
		});
	}, []);

	const showDialog = useCallback(
		<K extends DialogName>(
			name: K,
			options: DialogOptionsMap[K],
			behavior?: ShowDialogBehavior,
		) => {
			const spec = dialogRegistry[name];
			if (isMobile && spec.getMobileRoute) {
				const route = spec.getMobileRoute(options);
				if (behavior?.replace) {
					router.replace(route);
				} else {
					router.push(route);
				}
				return;
			}
			setActive({ name, options } as ActiveDialog);
		},
		[router, isMobile],
	);

	useEffect(() => {
		if (!(isMobile && active)) {
			return;
		}
		const spec = dialogRegistry[active.name];
		if (!spec.getMobileRoute) {
			return;
		}
		const route = spec.getMobileRoute(active.options as never);
		setActive(null);
		router.push(route);
	}, [isMobile, active, router]);

	const value = useMemo(
		() => ({ showDialog, hideDialog, active }),
		[showDialog, hideDialog, active],
	);

	return (
		<DialogContext.Provider value={value}>
			{children}
			{active &&
				dialogRegistry[active.name].render({
					options: active.options as never,
					onClose: () => hideDialog(active.name),
				})}
		</DialogContext.Provider>
	);
};
